// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, StyleSheet, Platform, TextInput,
} from 'react-native';
import { Colors, FontSize, FontWeight, Radii, Shadow } from '../../theme/tokens';

// Web-only staggered fade-up entrance (react-native-web animationKeyframes).
function fadeUp(delayMs: number): any {
  if (Platform.OS !== 'web') return null;
  return {
    animationKeyframes: { '0%': { opacity: 0, transform: [{ translateY: 14 }] }, '100%': { opacity: 1, transform: [{ translateY: 0 }] } },
    animationDuration: '500ms',
    animationDelay: `${delayMs}ms`,
    animationFillMode: 'both',
    animationTimingFunction: 'cubic-bezier(.2,.7,.3,1)',
  };
}
import { useResponsive } from '../../theme/responsive';
import { useApp } from '../../store/AppStore';
import { useAuth } from '../../store/AuthContext';
import { agentsService } from '../../services/agents/agents.service';
import { apiClient } from '../../services/api-client';
import { PLATFORM_ORG, PRODUCT } from '../../api-endpoints/api-endpoints';

// FE invite roles → platform invite roles (platform accepts only these two).
const ROLE_MAP: Record<string, string> = { Admin: 'org_admin', Agent: 'admin' };
import { ScreenShell } from '../../components/WebShell';
import { Icon } from '../../components/Icon';
import { Avatar } from '../../components/Avatar';
import PrimaryButton from '../../components/Button_Component/Primary_Button';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Agent {
  id?: string;             // tickets agent id (agt_…) — needed for remove / role update
  platformUserId?: string; // platform user behind this agent — used to de-dupe the member picker
  invitationId?: string;   // platform invitation id (inv_…) for pending, not-yet-accepted invites
  initials: string;
  name: string;
  email: string;
  role: string;
  last: string;
  color: string;
}

// ─── Seed Data (fallback only) ────────────────────────────────────────────────

const SEED_AGENTS: Agent[] = [];

const AGENT_COLORS = ['#f97316', '#10b981', '#a855f7', '#0ea5e9', '#06b6d4', '#84cc16', '#f59e0b'];

function mapApiAgent(a: any, idx: number): Agent {
  const name = a.displayName || a.name || a.email?.split('@')[0] || 'Agent';
  const initials = name.split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 2);
  const role = a.role ? (a.role.charAt(0).toUpperCase() + a.role.slice(1)) : 'Agent';
  const last = a.lastSeenAt
    ? new Date(a.lastSeenAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : (a.availability === 'online' ? 'online · just now' : 'offline');
  return {
    id: a.id,
    platformUserId: a.platformUserId,
    initials,
    name,
    email: a.email || '',
    role,
    last,
    color: AGENT_COLORS[idx % AGENT_COLORS.length],
  };
}

const PERMISSIONS = [
  { perm: 'View tickets', checks: ['✓', '✓', '✓', '✓'] },
  { perm: 'Reply on tickets', checks: ['✓', '✓', '✓', '—'] },
  { perm: 'Resolve / re-open', checks: ['✓', '✓', '✓', '—'] },
  { perm: 'Edit contacts', checks: ['✓', '✓', '✓', '—'] },
  { perm: 'Bulk export contacts', checks: ['✓', '✓', '—', '—'] },
  { perm: 'Edit email templates', checks: ['✓', '✓', '—', '—'] },
  { perm: 'Manage agents & roles', checks: ['✓', '—', '—', '—'] },
  { perm: 'Delete workspace', checks: ['✓', '—', '—', '—'] },
];

// ─── InviteModal ──────────────────────────────────────────────────────────────

function InviteModal({
  onClose, onInvite,
}: {
  onClose: () => void;
  onInvite: (email: string, role: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('Agent');
  const roles = ['Admin', 'Agent'];

  return (
    <TouchableOpacity style={styles.overlay} onPress={onClose} activeOpacity={1}>
      <TouchableOpacity style={styles.modal} activeOpacity={1} onPress={() => {}}>
        <View style={styles.modalHead}>
          <View style={styles.modalHeadLeft}>
            <View style={styles.modalHeadIcon}>
              <Icon name="UserCheck" size={18} color={Colors.accent} stroke={1.9} />
            </View>
            <View>
              <Text style={styles.modalTitle}>Invite agent</Text>
              <Text style={styles.modalSubtitle}>They'll get an email to join your workspace.</Text>
            </View>
          </View>
          <TouchableOpacity onPress={onClose}>
            <Icon name="X" size={14} color={Colors.ink3} />
          </TouchableOpacity>
        </View>
        <View style={styles.modalBody}>
          <View style={styles.field}>
            <Text style={styles.fieldLabel}>Email</Text>
            <TextInput
              style={styles.input}
              placeholder="teammate@acme.com"
              value={email}
              onChangeText={setEmail}
              keyboardType="email-address"
              autoCapitalize="none"
            />
          </View>
          <View style={styles.field}>
            <Text style={styles.fieldLabel}>Role</Text>
            <View style={styles.roleRow}>
              {roles.map(r => (
                <TouchableOpacity
                  key={r}
                  style={[styles.rolePill, role === r && styles.rolePillActive]}
                  onPress={() => setRole(r)}
                >
                  <Text style={[styles.rolePillText, role === r && styles.rolePillTextActive]}>{r}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </View>
        <View style={styles.modalFoot}>
          <PrimaryButton title="Cancel" size="md" variant="default" onPress={onClose} />
          <PrimaryButton
            title="Send invite"
            size="md"
            variant="primary"
            leftIcon={<Icon name="Mail" size={13} color={Colors.paper} />}
            disabled={!email.trim()}
            onPress={() => { onInvite(email.trim(), role); onClose(); }}
          />
        </View>
      </TouchableOpacity>
    </TouchableOpacity>
  );
}

// ─── AddAgentModal ──────────────────────────────────────────────────────────
// Manual "Add agent" — pick an existing platform org member and register them
// as a ticketing agent so they become assignable. The assignee check requires
// the agent's user to be an active org member, so we source candidates straight
// from the org's membership list (and hide anyone who is already an agent).

const AGENT_GROUPS = ['support', 'billing', 'sales'];

function AddAgentModal({
  org, existing, onClose, onAdded, toast,
}: {
  org: any;
  existing: Agent[];
  onClose: () => void;
  onAdded: () => void;
  toast: (msg: string, tone?: string, icon?: string) => void;
}) {
  const [members, setMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<any>(null);
  const [displayName, setDisplayName] = useState('');
  const [group, setGroup] = useState('support');
  const [saving, setSaving] = useState(false);

  const memberName = (m: any) =>
    [m.user?.first_name, m.user?.last_name].filter(Boolean).join(' ').trim()
    || (m.user?.email || '').split('@')[0];

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res: any = await apiClient.get(PLATFORM_ORG.members(org.id));
        const raw = res?.data || res || [];
        const haveIds = new Set(existing.map(a => a.platformUserId).filter(Boolean));
        const haveEmails = new Set(existing.map(a => (a.email || '').toLowerCase()).filter(Boolean));
        const avail = (Array.isArray(raw) ? raw : []).filter((m: any) =>
          (m.status ? m.status === 'active' : true)
          && !haveIds.has(m.user_id)
          && !haveEmails.has((m.user?.email || '').toLowerCase()),
        );
        if (alive) { setMembers(avail); setLoading(false); }
      } catch {
        if (alive) { setMembers([]); setLoading(false); }
      }
    })();
    return () => { alive = false; };
  }, []);

  const pick = (m: any) => { setSelected(m); setDisplayName(memberName(m)); };

  const submit = async () => {
    if (!selected || !displayName.trim() || saving) return;
    setSaving(true);
    try {
      await agentsService.create({
        platformUserId: selected.user_id,
        displayName: displayName.trim(),
        email: selected.user?.email,
        groups: [group],
      } as any);
      toast(`${displayName.trim()} added as agent.`, 'success', 'check');
      onAdded();
      onClose();
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.response?.data?.title || 'Failed to add agent.';
      toast(msg, 'danger', 'x');
      setSaving(false);
    }
  };

  return (
    <TouchableOpacity style={styles.overlay} onPress={onClose} activeOpacity={1}>
      <TouchableOpacity style={styles.modal} activeOpacity={1} onPress={() => {}}>
        <View style={styles.modalHead}>
          <Text style={styles.modalTitle}>Add agent</Text>
          <TouchableOpacity onPress={onClose}>
            <Icon name="X" size={14} color={Colors.ink3} />
          </TouchableOpacity>
        </View>
        <View style={styles.modalBody}>
          <View style={styles.field}>
            <Text style={styles.fieldLabel}>Team member</Text>
            {loading ? (
              <Text style={styles.helpText}>Loading members…</Text>
            ) : members.length === 0 ? (
              <Text style={styles.helpText}>
                Everyone in your organization is already an agent. Invite a teammate first.
              </Text>
            ) : (
              <View style={styles.memberList}>
                {members.map((m: any) => {
                  const active = selected?.user_id === m.user_id;
                  return (
                    <TouchableOpacity
                      key={m.user_id}
                      style={[styles.memberRow, active && styles.memberRowActive]}
                      onPress={() => pick(m)}
                    >
                      <View style={{ flex: 1 }}>
                        <Text style={styles.agentName}>{memberName(m)}</Text>
                        <Text style={styles.agentEmail}>{m.user?.email} · {m.role}</Text>
                      </View>
                      {active && <Icon name="Check" size={14} color={Colors.accent} />}
                    </TouchableOpacity>
                  );
                })}
              </View>
            )}
          </View>

          {selected && (
            <>
              <View style={styles.field}>
                <Text style={styles.fieldLabel}>Display name</Text>
                <TextInput style={styles.input} value={displayName} onChangeText={setDisplayName} />
              </View>
              <View style={styles.field}>
                <Text style={styles.fieldLabel}>Group</Text>
                <View style={styles.roleRow}>
                  {AGENT_GROUPS.map(g => (
                    <TouchableOpacity
                      key={g}
                      style={[styles.rolePill, group === g && styles.rolePillActive]}
                      onPress={() => setGroup(g)}
                    >
                      <Text style={[styles.rolePillText, group === g && styles.rolePillTextActive]}>{g}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            </>
          )}
        </View>
        <View style={styles.modalFoot}>
          <PrimaryButton title="Cancel" size="md" variant="default" onPress={onClose} />
          <PrimaryButton
            title={saving ? 'Adding…' : 'Add agent'}
            size="md"
            variant="primary"
            leftIcon={<Icon name="Plus" size={13} color={Colors.paper} />}
            disabled={!selected || !displayName.trim() || saving}
            onPress={submit}
          />
        </View>
      </TouchableOpacity>
    </TouchableOpacity>
  );
}

// ─── AgentsScreen ─────────────────────────────────────────────────────────────

export function AgentsScreen() {
  const { toast, copy } = useApp();
  const { org } = useAuth();
  const { isMobile, pagePadX } = useResponsive();
  const [agents, setAgents] = useState<Agent[]>(SEED_AGENTS);
  const [showInvite, setShowInvite] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [hoverEmail, setHoverEmail] = useState<string | null>(null);

  // Load agents from the tickets API + pending invitations from the platform,
  // so freshly-invited teammates show as "invited · pending" until they accept.
  const loadAgents = async () => {
    const [agentsRes, invitesRes] = await Promise.allSettled([
      agentsService.list(),
      org?.id
        ? apiClient.get(`${PLATFORM_ORG.invitations(org.id)}?status=pending`)
        : Promise.resolve(null),
    ]);

    let rows: Agent[] = [];
    if (agentsRes.status === 'fulfilled') {
      const raw = (agentsRes.value as any)?.data || agentsRes.value || [];
      if (Array.isArray(raw)) rows = raw.map(mapApiAgent);
    }

    if (invitesRes.status === 'fulfilled' && invitesRes.value) {
      const invs = (invitesRes.value as any)?.data || invitesRes.value || [];
      const haveEmails = new Set(rows.map(a => (a.email || '').toLowerCase()));
      (Array.isArray(invs) ? invs : []).forEach((inv: any, i: number) => {
        if (inv.status && inv.status !== 'pending') return;
        const email = inv.email || '';
        if (!email || haveEmails.has(email.toLowerCase())) return;
        const local = email.split('@')[0];
        rows.push({
          id: undefined,
          platformUserId: undefined,
          invitationId: inv.id,
          initials: local.slice(0, 2).toUpperCase(),
          name: local.charAt(0).toUpperCase() + local.slice(1),
          email,
          role: inv.role === 'org_admin' ? 'Admin' : 'Agent',
          last: 'invited · pending',
          color: AGENT_COLORS[(rows.length + i) % AGENT_COLORS.length],
        });
      });
    }
    setAgents(rows);
  };
  useEffect(() => { loadAgents(); }, []);

  const invite = async (email: string, role: string) => {
    if (!org?.id) {
      toast('No organization context — please re-login.', 'danger', 'x');
      return;
    }
    const platformRole = ROLE_MAP[role] || 'admin';
    try {
      // Teammate invitations live in platform-api. It creates the invite,
      // emails the accept link, and returns the token (so we can also copy
      // the link as a fallback if email delivery is unavailable).
      const res: any = await apiClient.post(PLATFORM_ORG.invitations(org.id), {
        email,
        role: platformRole,
        // Scope the invite to the tickets product so the platform sends the
        // SK Tickets agent-invitation template (not the default 'works' one)
        // and the accept link lands on the tickets app.
        products: [PRODUCT],
      });
      const token = res?.data?.token;
      const local = email.split('@')[0];
      const initials = local.slice(0, 2).toUpperCase();
      setAgents(a => [...a, {
        initials, name: local.charAt(0).toUpperCase() + local.slice(1),
        email, role, last: 'invited · pending', color: '#84cc16',
      }]);
      if (token && typeof window !== 'undefined') {
        const link = `${window.location.origin}/#/accept-invite?token=${encodeURIComponent(token)}&email=${encodeURIComponent(email)}`;
        copy(link, 'invite link');
        toast(`Invite sent to ${email} — link copied as backup.`, 'success', 'mail');
      } else {
        toast(`Invite sent to ${email}.`, 'success', 'mail');
      }
      // Reconcile with the server so the pending invitation persists across reloads.
      loadAgents();
    } catch (e: any) {
      const msg = e?.response?.data?.title || e?.response?.data?.message || 'Failed to send invite.';
      toast(msg, 'danger', 'x');
    }
  };

  const remove = async (email: string) => {
    const agent = agents.find(a => a.email === email);
    if (agent?.id) {
      try { await agentsService.delete(agent.id); } catch {}
    }
    setAgents(a => a.filter(x => x.email !== email));
    toast('Agent removed.', 'info');
  };

  const changeRole = async (email: string, role: string) => {
    const agent = agents.find(a => a.email === email);
    setAgents(a => a.map(x => x.email === email ? { ...x, role } : x));
    if (agent?.id) {
      try {
        await agentsService.update(agent.id, { groups: [role.toLowerCase().replace('-', '_')] });
      } catch (e: any) {
        // Role display is updated locally even if API fails
      }
    }
    toast(`Role updated to ${role}.`, 'success', 'check');
  };

  const roles = ['Owner', 'Admin', 'Agent', 'Read-only'];

  // On mobile the fixed-width table columns can overflow; wrap each table in a
  // horizontal scroll so the layout never breaks. On wider screens render directly.
  const TableWrap = isMobile
    ? (props: any) => (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ minWidth: 720 }}>
          {props.children}
        </ScrollView>
      )
    : React.Fragment;

  return (
    <ScreenShell crumbs={[
      { label: 'Settings' },
      { label: 'Agents & roles' },
    ]}>
      <View style={styles.root}>
        <View style={[styles.pageHead, { paddingHorizontal: pagePadX, flexWrap: 'wrap' }]}>
          <View style={{ flex: 1 }}>
            <Text style={styles.pageTitle}>Agents & roles</Text>
            <Text style={styles.pageSub}>Invite teammates, assign roles, and control what each agent can see and do.</Text>
          </View>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <PrimaryButton
              title="Add agent"
              size="md"
              variant="default"
              leftIcon={<Icon name="Plus" size={14} color={Colors.ink2} />}
              onPress={() => setShowAdd(true)}
            />
            <PrimaryButton
              title="Invite agent"
              size="md"
              variant="primary"
              leftIcon={<Icon name="Mail" size={14} color={Colors.paper} />}
              onPress={() => setShowInvite(true)}
            />
          </View>
        </View>

        <ScrollView style={styles.body} contentContainerStyle={[styles.bodyContent, { padding: isMobile ? 16 : 24 }]}>
          <Text style={styles.sectionTitle}>
            {agents.length} agents · {agents.filter(a => a.role !== 'Read-only').length} licensed
          </Text>

          {/* Agents Table */}
          <TableWrap>
          <View style={[styles.card, fadeUp(40)]}>
            <View style={[styles.tableRow, styles.tableHeader]}>
              <Text style={[styles.thText, { flex: 2 }]}>Agent</Text>
              <Text style={[styles.thText, { width: 160 }]}>Role</Text>
              <Text style={[styles.thText, { width: 180 }]}>Last activity</Text>
              <View style={{ width: 80 }} />
            </View>
            {agents.map((a, i) => {
              const online = typeof a.last === 'string' && a.last.toLowerCase().includes('online');
              return (
              <View key={a.email}
                {...(Platform.OS === 'web' ? { onMouseEnter: () => setHoverEmail(a.email), onMouseLeave: () => setHoverEmail(null) } as any : {})}
                style={[styles.tableRow, i < agents.length - 1 && styles.rowBorder, hoverEmail === a.email && styles.rowHover]}>
                <View style={[styles.cellAgent, { flex: 2 }]}>
                  <View>
                    <Avatar initials={a.initials} color={a.color} size="md" />
                    {online && <View style={styles.onlineDot} />}
                  </View>
                  <View>
                    <Text style={styles.agentName}>{a.name}</Text>
                    <Text style={styles.agentEmail}>{a.email}</Text>
                  </View>
                </View>
                <View style={{ width: 160 }}>
                  <View style={styles.selectFake}>
                    {roles.map(r => null)}
                    <Text style={styles.selectText}>{a.role}</Text>
                    <TouchableOpacity
                      onPress={() => {
                        const idx = roles.indexOf(a.role);
                        const next = roles[(idx + 1) % roles.length];
                        changeRole(a.email, next);
                      }}
                    >
                      <Icon name="Chevron" size={12} color={Colors.ink3} />
                    </TouchableOpacity>
                  </View>
                </View>
                <View style={[styles.lastCell, { width: 180 }]}>
                  <View style={[styles.lastDot, { backgroundColor: online ? Colors.success : Colors.ink4 }]} />
                  <Text style={[styles.lastText, online && { color: Colors.ink2, fontWeight: FontWeight.medium }]}>{a.last}</Text>
                </View>
                <View style={{ width: 80, alignItems: 'flex-end' }}>
                  <TouchableOpacity
                    style={[styles.removeBtn, a.role === 'Owner' && { opacity: 0.3 }]}
                    disabled={a.role === 'Owner'}
                    onPress={() => remove(a.email)}
                  >
                    <Icon name="X" size={13} color={Colors.ink3} />
                  </TouchableOpacity>
                </View>
              </View>
              );
            })}
          </View>
          </TableWrap>

          <Text style={[styles.sectionTitle, { marginTop: 8 }]}>Role permissions</Text>
          <Text style={styles.sectionSub}>What each role can do.</Text>

          {/* Permissions Matrix */}
          <TableWrap>
          <View style={[styles.card, { marginTop: 8 }, fadeUp(120)]}>
            <View style={[styles.tableRow, styles.tableHeader]}>
              <Text style={[styles.thText, { flex: 2 }]}>Permission</Text>
              {['Owner', 'Admin', 'Agent', 'Read-only'].map(r => (
                <Text key={r} style={[styles.thText, styles.thCenter, { width: 80 }]}>{r}</Text>
              ))}
            </View>
            {PERMISSIONS.map((row, i) => (
              <View key={row.perm} style={[styles.tableRow, i < PERMISSIONS.length - 1 && styles.rowBorder]}>
                <Text style={[styles.permText, { flex: 2 }]}>{row.perm}</Text>
                {row.checks.map((c, j) => (
                  <View key={j} style={{ width: 80, alignItems: 'center' }}>
                    {c === '✓' ? (
                      <View style={styles.permYes}>
                        <Icon name="Check" size={12} color={Colors.success} stroke={2.8} />
                      </View>
                    ) : (
                      <Text style={styles.permNo}>—</Text>
                    )}
                  </View>
                ))}
              </View>
            ))}
          </View>
          </TableWrap>
        </ScrollView>

        {showInvite && (
          <InviteModal onClose={() => setShowInvite(false)} onInvite={invite} />
        )}
        {showAdd && (
          <AddAgentModal
            org={org}
            existing={agents}
            onClose={() => setShowAdd(false)}
            onAdded={loadAgents}
            toast={toast}
          />
        )}
      </View>
    </ScreenShell>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: Colors.paper },
  pageHead: {
    flexDirection: 'row', alignItems: 'flex-start', gap: 16,
    paddingHorizontal: 28, paddingTop: 20, paddingBottom: 18,
    borderBottomWidth: 1, borderBottomColor: Colors.line,
    backgroundColor: Colors.card, flexShrink: 0,
  },
  pageTitle: { fontSize: 20, fontWeight: FontWeight.semibold, color: Colors.ink, letterSpacing: -0.2 },
  pageSub: { fontSize: 13, color: Colors.ink3, marginTop: 4 },
  body: { flex: 1 },
  bodyContent: { maxWidth: 1000, alignSelf: 'center', width: '100%' as any, padding: 24 },
  sectionTitle: { fontSize: 14, fontWeight: FontWeight.semibold, color: Colors.ink },
  sectionSub: { fontSize: 12.5, color: Colors.ink3, marginTop: 2, marginBottom: 8 },
  card: {
    backgroundColor: Colors.card, borderRadius: Radii.r4,
    borderWidth: 1, borderColor: Colors.line,
    marginTop: 8, marginBottom: 16, overflow: 'hidden',
    ...(Shadow.base as object),
  },
  tableRow: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 18, paddingVertical: 13, ...(Platform.OS === 'web' ? { transitionProperty: 'background-color, box-shadow', transitionDuration: '160ms', transitionTimingFunction: 'cubic-bezier(.2,.7,.3,1)' } as any : {}) },
  tableHeader: { backgroundColor: Colors.paper2, borderBottomWidth: 1, borderBottomColor: Colors.line, paddingVertical: 10 },
  thText: { fontSize: 11.5, fontWeight: FontWeight.semibold, color: Colors.ink3 },
  thCenter: { textAlign: 'center' },
  rowBorder: { borderBottomWidth: 1, borderBottomColor: Colors.line },
  rowHover: {
    backgroundColor: Colors.paper2,
    ...(Platform.OS === 'web'
      ? {
          transitionProperty: 'background-color, box-shadow',
          transitionDuration: '160ms',
          transitionTimingFunction: 'cubic-bezier(.2,.7,.3,1)',
          boxShadow: `inset 3px 0 0 ${Colors.accent}, 0 1px 0 rgba(16,24,40,0.04)`,
        } as any
      : {}),
  },
  cellAgent: { flexDirection: 'row', alignItems: 'center', gap: 11 },
  agentName: { fontSize: 13.5, fontWeight: FontWeight.semibold, color: Colors.ink, letterSpacing: -0.1 },
  agentEmail: {
    fontSize: 11, color: Colors.ink3,
    fontFamily: Platform.OS === 'web' ? 'monospace' : undefined,
    backgroundColor: Colors.paper2,
    borderWidth: 1, borderColor: Colors.line,
    borderRadius: Radii.r1,
    paddingHorizontal: 7, paddingVertical: 2,
    alignSelf: 'flex-start',
    marginTop: 4,
    overflow: 'hidden',
  },
  onlineDot: {
    position: 'absolute', right: -1, bottom: -1, width: 10, height: 10, borderRadius: Radii.full,
    backgroundColor: Colors.success, borderWidth: 2, borderColor: Colors.card,
  },
  lastCell: { flexDirection: 'row', alignItems: 'center', gap: 7 },
  lastDot: { width: 7, height: 7, borderRadius: Radii.full },
  selectFake: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    borderWidth: 1, borderColor: Colors.line,
    borderRadius: Radii.r2, paddingHorizontal: 8, paddingVertical: 5,
    backgroundColor: Colors.card, width: 130,
  },
  selectText: { fontSize: 12.5, color: Colors.ink },
  lastText: { fontSize: 12, color: Colors.ink3 },
  removeBtn: {
    width: 28, height: 28, justifyContent: 'center', alignItems: 'center',
    borderRadius: Radii.r1,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : {}),
  },
  permText: { fontSize: 13, color: Colors.ink },
  checkText: { fontSize: 14, fontWeight: FontWeight.semibold, textAlign: 'center' },
  permYes: {
    width: 22, height: 22, borderRadius: Radii.full,
    backgroundColor: Colors.successSoft, borderWidth: 1, borderColor: Colors.successLine,
    alignItems: 'center', justifyContent: 'center',
  },
  permNo: { fontSize: 14, color: Colors.ink4 },
  // Modal
  overlay: {
    position: 'absolute' as any, top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(16,24,40,0.42)',
    justifyContent: 'center', alignItems: 'center',
    zIndex: 100, padding: 24,
  },
  modal: {
    width: 480, maxWidth: '90%', backgroundColor: Colors.card,
    borderRadius: Radii.r5,
    overflow: 'hidden',
    ...(Shadow.lg as object),
  },
  modalHead: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    padding: 20, borderBottomWidth: 1, borderBottomColor: Colors.line,
  },
  modalHeadLeft: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  modalHeadIcon: {
    width: 38, height: 38, borderRadius: Radii.r3,
    backgroundColor: Colors.accentSoft, borderWidth: 1, borderColor: Colors.accentLine,
    alignItems: 'center', justifyContent: 'center',
  },
  modalTitle: { fontSize: 19, fontWeight: FontWeight.bold, color: Colors.ink, letterSpacing: -0.2 },
  modalSubtitle: { fontSize: 12, color: Colors.ink3, marginTop: 2 },
  modalBody: { padding: 20, gap: 16 },
  modalFoot: {
    flexDirection: 'row', justifyContent: 'flex-end', gap: 8,
    padding: 14, borderTopWidth: 1, borderTopColor: Colors.line,
    backgroundColor: Colors.paper2,
  },
  field: { gap: 4 },
  fieldLabel: { fontSize: 13, fontWeight: FontWeight.medium, color: Colors.ink },
  input: {
    borderWidth: 1, borderColor: Colors.lineStrong,
    borderRadius: Radii.r3, paddingHorizontal: 12, paddingVertical: 10,
    fontSize: 13.5, color: Colors.ink, backgroundColor: Colors.card,
    ...(Platform.OS === 'web' ? { outlineStyle: 'none', boxShadow: 'inset 0 1px 2px rgba(16,24,40,0.04)' } as any : {}),
  },
  roleRow: { flexDirection: 'row', gap: 8 },
  rolePill: {
    paddingHorizontal: 16, paddingVertical: 8,
    borderRadius: Radii.full, borderWidth: 1, borderColor: Colors.line,
    backgroundColor: Colors.paper2,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : {}),
  },
  rolePillActive: { backgroundColor: Colors.accent, borderColor: Colors.accent },
  rolePillText: { fontSize: 13, fontWeight: FontWeight.medium, color: Colors.ink2 },
  rolePillTextActive: { color: Colors.white, fontWeight: FontWeight.semibold },
  helpText: { fontSize: 12.5, color: Colors.ink3, paddingVertical: 6 },
  memberList: {
    borderWidth: 1, borderColor: Colors.line, borderRadius: Radii.r2,
    maxHeight: 220, overflow: 'hidden',
  },
  memberRow: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    paddingHorizontal: 12, paddingVertical: 9,
    borderBottomWidth: 1, borderBottomColor: Colors.line,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : {}),
  },
  memberRowActive: { backgroundColor: Colors.paper2 },
});
